import { AxiosError } from 'axios'
import {
  ApprovalActionType,
  ApprovalStateApprover,
  ApprovalStatePolicy,
  NextRequiredApprover,
  StaleStateError,
} from '@/types/approvals'

export function isStaleStateError(error: unknown): boolean {
  const response = (error as AxiosError<StaleStateError>)?.response
  return response?.status === 409 && response.data?.error_code === 'stale_state'
}

/** The attach endpoint 400s when a policy is switched off or in another project. */
export function isPolicyNotAttachableError(error: unknown): boolean {
  return (error as AxiosError)?.response?.status === 400
}

export function isForbiddenError(error: unknown): boolean {
  return (error as AxiosError)?.response?.status === 403
}

export function isNotFoundError(error: unknown): boolean {
  return (error as AxiosError)?.response?.status === 404
}

export function approvalsRemaining(policy: ApprovalStatePolicy): number {
  const required = policy.approvals_required ?? policy.approvers.length
  return Math.max(required - policy.approvals_received, 0)
}

export type ApprovalEligibility = {
  /** Whether the viewer is a named approver on this policy at all. */
  isApprover: boolean
  /** The viewer's most recent recorded action on this policy, if any. */
  latestActionType: ApprovalActionType | null
  /** Whether the viewer may approve or reject this policy right now. */
  canAct: boolean
}

const NOT_ELIGIBLE: ApprovalEligibility = {
  isApprover: false,
  latestActionType: null,
  canAct: false,
}

/** Whether any of this entry's rows names the viewer: the user themself, or a slot they hold. */
function entryHasViewer(
  approver: ApprovalStateApprover,
  viewerId: string,
): boolean {
  return approver.kind === 'user'
    ? approver.user_id === viewerId
    : approver.slots.some((slot) => slot.user_ids.includes(viewerId))
}

/** Whether every row of this entry has acted — a lone user, or every one of a position's slots. */
function isEntryDecided(approver: ApprovalStateApprover): boolean {
  return approver.kind === 'user'
    ? !!approver.latest_action
    : approver.satisfied
}

/** The entry `next_required` points at, or undefined when it names one this policy doesn't have. */
function namedEntry(
  policy: ApprovalStatePolicy,
  named: NextRequiredApprover,
): ApprovalStateApprover | undefined {
  return policy.approvers.find((approver) =>
    named.kind === 'user'
      ? approver.kind === 'user' && approver.user_id === named.user_id
      : approver.kind === 'position' &&
        approver.position_id === named.position_id,
  )
}

/**
 * The viewer's own decision on one entry, or null while they still owe it one.
 *
 * A position entry can hold the viewer in more than one slot: slots merge only
 * when their eligible users match exactly, so someone on both a {A, B} and a
 * {B, C} slot sits in two. They have decided only once every slot they hold
 * has an action on it — otherwise the one they haven't answered is still
 * waiting on them.
 */
function viewerActionType(
  approver: ApprovalStateApprover,
  viewerId: string,
): ApprovalActionType | null {
  if (approver.kind === 'user') return approver.latest_action?.type ?? null

  const held = approver.slots.filter((slot) => slot.user_ids.includes(viewerId))
  if (!held.length || held.some((slot) => !slot.latest_action)) return null

  return held.reduce((latest, slot) =>
    slot.latest_action!.created_at > latest.latest_action!.created_at
      ? slot
      : latest,
  ).latest_action!.type
}

/**
 * Whether it's the viewer's turn on an open sequential policy.
 *
 * `next_required` is the service's own answer and wins whenever it names one
 * of the policy's approvers — a user outright, or a position, in which case
 * every current holder of one of its slots is on their turn. It can also
 * arrive null or pointing at nobody, so fall back to the rule it encodes: the
 * earliest entry by `sort_order` that hasn't been fully decided. A rejected
 * or satisfied policy has no turn to take, and deriving one there would hand
 * actions to an approver the service is deliberately not waiting on.
 */
function isSequentialTurn(
  policy: ApprovalStatePolicy,
  viewerId: string,
): boolean {
  const named = policy.next_required
    ? namedEntry(policy, policy.next_required)
    : undefined
  if (named) return entryHasViewer(named, viewerId)

  if (policy.is_rejected || policy.satisfied) return false

  const undecided = policy.approvers.filter(
    (approver) => !isEntryDecided(approver),
  )
  if (!undecided.length) return false

  const earliest = undecided.reduce((earliest, approver) =>
    approver.sort_order < earliest.sort_order ? approver : earliest,
  )
  return entryHasViewer(earliest, viewerId)
}

/**
 * Derives what the viewer may do on one approval policy right now.
 *
 * A sequential policy only allows acting on your turn, or to change a
 * decision you already recorded — someone whose turn hasn't come up yet
 * gets no actions. An any-order policy lets an approver act while the
 * policy is still open, or at any time to change a decision they already
 * recorded — but once the quorum is met without them, there's nothing left
 * for them to do. An unresolved policy config means eligibility can't be
 * determined, so no actions are offered.
 */
export function getApprovalEligibility(
  policy: ApprovalStatePolicy,
  viewerId?: string,
): ApprovalEligibility {
  if (!viewerId) return NOT_ELIGIBLE

  const viewerApprover = policy.approvers.find((approver) =>
    entryHasViewer(approver, viewerId),
  )
  if (!viewerApprover) return NOT_ELIGIBLE

  const latestActionType = viewerActionType(viewerApprover, viewerId)

  if (!policy.config_resolved) {
    return { isApprover: true, latestActionType, canAct: false }
  }

  const hasDecided = latestActionType !== null

  if (policy.approval_order === 'sequential') {
    return {
      isApprover: true,
      latestActionType,
      canAct: isSequentialTurn(policy, viewerId) || hasDecided,
    }
  }

  return {
    isApprover: true,
    latestActionType,
    canAct: hasDecided || !policy.satisfied,
  }
}
